<template>
  <div class="w-fit mx-auto">
    <CommonSpinner size="2rem" />
  </div>
</template>

<script setup lang="ts">
import type { GetStartedService } from "../../types";

const route = useRoute();
const getStartedModalStore = useGetStartedModalStore();
const mapStore = useMapStore();

/**
 * Selected service entity.
 */
const selectedService = computed(() => getStartedModalStore.selectedService);

/**
 * Get clinic slug from route query params.
 */
const selectedClinicSlug = computed(() => route.query.slug);

/**
 * Fetch clinic detail by selected slug.
 */
const fetchClinicDetailBySlug = async () => {
  if (!selectedClinicSlug.value) return;

  const { data: clinicDetail } = await useFetchClinicDetailBySlug(selectedClinicSlug.value);
  const clinic = clinicDetail.value?.data;
  if (!clinic) return;

  mapStore.setSelectedClinic({
    id: clinic.id,
    title: clinic.display_title,
    slug: clinic.slug,
  });
  await fetchClinicTests(clinic.id);
};

/**
 * Fetch all tests related to a specific clinic.
 * Get started → instant test should show both `instant-test` and `clinic-test` products,
 * but `clinic-test` is only requested when that service is enabled site-wide so
 * deployments without it keep their previous behaviour.
 * @param clinic - Clinic Id from where we should fetch tests.
 */
const fetchClinicTests = async (clinic: string) => {
  mapStore.setPanelTestsLoading(true);
  try {
    const { data: servicesResult } = await useServiceListType();
    const services: GetStartedService[] = servicesResult.value?.data ?? [];
    const isClinicTestEnabled = services.some((service) => service.slug === "clinic-test");

    const params: Record<string, string> = {
      clinic,
      "service_type[0]": "instant-test",
    };
    if (isClinicTestEnabled) {
      params["service_type[1]"] = "clinic-test";
    }

    const { data: productsList } = await useFetchProductList(params);
    mapStore.getTests(productsList.value?.data ?? []);
  } finally {
    mapStore.setPanelTestsLoading(false);
  }
};

/**
 * Watch selected clinic slug change in order to update the products.
 */
watch(selectedClinicSlug, fetchClinicDetailBySlug);

/**
 * Auto call at first render
 */
await fetchClinicDetailBySlug();

function openModal() {
  getStartedModalStore.navigate("select_clinic");
  getStartedModalStore.open(
    // @ts-ignore
    {
      clinic_view: "map",
      ...selectedService.value,
      content: "",
    }
  );
}

async function abort() {
  navigateTo({
    params: {
      ...route.params,
      service: "",
    },
  });
}

onMounted(openModal);
watch(
  () => getStartedModalStore.isOpen,
  (isOpen, wasOpen) => {
    if (wasOpen && !isOpen) abort();
  }
);

onUnmounted(getStartedModalStore.close);
</script>
